import {useCallback, useEffect, useRef} from 'react'
import {useLocation} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../../../hooks/react-redux'
import {useThrottle} from '../../../../hooks/useThrottle'
import {restApi, VsStatusType} from '../../../../services/rest'
import {subscribeOnProjectEvents} from '../../../../utils/subscriber'
import {VERSIONED_SETTINGS_STATUS_UPDATED} from '../../../../utils/subscriptionEvents'
import {useBanners} from '../contexts/Banners/BannersContext'
import {getOriginalVersionedSettings} from '../EditPipelinePage/EditPipelinePage.selectors'
import {pipelineDraft} from '../EditPipelinePage/slices/EditPipelinePage.slices'
import {AlertMessageType} from '../types/AlertMessages'

import {usePipelineId, usePipelineInternalId} from './pipeline'
import {useIsParentKotlinDsl} from './useIsParentKotlinDsl'

const THROTTLER_TIMEOUT = 15000

function toHealthItemType(type: VsStatusType) {
  switch (type) {
    case VsStatusType.INFO:
      return AlertMessageType.SUCCESS
    case VsStatusType.WARN:
      return AlertMessageType.ERROR
    default:
      return AlertMessageType.INFO
  }
}

export function useVersionedSettingsStatus() {
  const pipelineId = usePipelineId()
  const pipelineInternalId = usePipelineInternalId(pipelineId)
  const {addBanner, removeBanner} = useBanners()
  const versionedSettings = useAppSelector(getOriginalVersionedSettings(pipelineId))
  const isKotlinDsl = versionedSettings?.format === 'kotlin'
  const isParentKotlinDsl = useIsParentKotlinDsl()
  const [fetch, {status}] = restApi.endpoints.getVsStatus.useLazyQuery({
    selectFromResult: ({data}) => ({
      status: data,
    }),
  })
  const fetchVsStatus = useThrottle(fetch, THROTTLER_TIMEOUT)
  const location = useLocation()
  const showInfoMessagesRef = useRef(location?.state?.isCreated ?? false)

  const bannerKey = `${pipelineId}-${status?.timestamp}`
  const dispatch = useAppDispatch()
  const saveOnServerHandler = useCallback(() => {
    dispatch(
      pipelineDraft.actions.setVersionedSettings({
        id: pipelineId,
        storedInRepo: false,
      }),
    )
    removeBanner(pipelineId, bannerKey)
  }, [dispatch, pipelineId, removeBanner, bannerKey])

  useEffect(() => {
    if (!pipelineInternalId) {
      return undefined
    }
    return subscribeOnProjectEvents(pipelineInternalId, [VERSIONED_SETTINGS_STATUS_UPDATED], () => {
      fetchVsStatus({projectLocator: pipelineId, fields: 'message,type,timestamp'})
    })
  }, [fetchVsStatus, pipelineId, pipelineInternalId])

  useEffect(() => {
    if (status?.type === VsStatusType.WARN) {
      addBanner(pipelineId, {
        key: bannerKey,
        message: status.message,
        type: toHealthItemType(status.type),
        actions:
          isKotlinDsl && isParentKotlinDsl
            ? undefined
            : [{label: 'Save on TeamCity server', onClick: saveOnServerHandler}],
      })
      return () => removeBanner(pipelineId, bannerKey)
    } else if (
      showInfoMessagesRef.current &&
      status?.type === VsStatusType.INFO &&
      status.message.startsWith('TeamCity change')
    ) {
      addBanner(pipelineId, {
        key: bannerKey,
        title: 'Initial commit will be made shortly.',
        message: status.message,
        type: toHealthItemType(status.type),
        closeable: true,
      })
      showInfoMessagesRef.current = false
      return () => removeBanner(pipelineId, bannerKey)
    }
    return undefined
  }, [
    status,
    addBanner,
    saveOnServerHandler,
    pipelineId,
    bannerKey,
    removeBanner,
    isKotlinDsl,
    isParentKotlinDsl,
  ])
}
